import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { MathJaxContext } from 'better-react-mathjax';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  setUserData as setUserDataAction,
  setUserDataWhenUnAuthorised as setUserDataWhenUnAuthorisedAction,
} from '../../redux/actionAndReducers/user/userActions';
import {
  AskSignUpType,
  QuizEntityDataType,
  QuizType,
} from '../../types/quiz';

// eslint-disable-next-line import/no-named-as-default
import { updateQuizData as updateQuizDataAction } from '../../redux/actionAndReducers/quizData/quizDataAction';
import { RootState } from '../../redux/store';
import { UserRole } from '../../utils/enums';
import { fetchData } from '../../utils/fetch';
import Loader from '../common/loader/Loader';
import useUnload from '../common/useUnload';
import { getTimeDifferenceAction } from '../../redux/actionAndReducers/common/getTimeDifference';

import { addContext } from '../../services/DatadogService';

const config = {
  loader: { load: ['[tex]/html'] },
  tex: {
    packages: { '[+]': ['html'] },
    inlineMath: [
      ['$', '$'],
      ['\\(', '\\)'],
    ],
    displayMath: [
      ['$$', '$$'],
      ['\\[', '\\]'],
    ],
  },
};

const mapDispatchToProps = {
  updateQuizData: updateQuizDataAction,
  setUserData: setUserDataAction,
  getTimeDifference: getTimeDifferenceAction,
  setUserDataWhenUnAuthorised: setUserDataWhenUnAuthorisedAction,
};

const mapStateToProps = (state: RootState) => ({
  userData: state.userData,
  userId: state.userData.userId,
  isUserApiSuccess: state.userData.success,
  isGuestUser: state.userData.guestUser,
  userName: state.userData.name,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>

interface QuizContainerProps extends PropsFromRedux {
  quizId: string | undefined,
  quizData: QuizEntityDataType,
  asyncQuiz: boolean,
  quizEnded: boolean,
  quizInactive: boolean,
}

const unloadFn = (e: any) => {
  e.returnValue = '';
};

const outer = (quizId: number) => async function (e: any) {
  await fetchData({
    method: 'POST',
    data: {
      quizId,
    },
    url: '/async-mode/v1/update-leave-time',
    credentials: 'include',
  });
  e.returnValue = '';
};

const PresenterMainComponent = dynamic(() => import('../Presenter/PresenterMainComponent/PresenterMainComponent'));
const StudentContainer = dynamic(() => import('../StudentContainer/StudentContainer'));
const AsyncStudentContainer = dynamic(() => import('../AsyncStudentContainer/AsyncStudentContainer'));
const QuizEnded = dynamic(() => import('./QuizEnded/QuizEnded'));
const QuizInactive = dynamic(() => import('./QuizInactive/QuizInactive'));

const QuizContainer: FunctionComponent<QuizContainerProps> = ({
  quizId,
  quizData: quizDataNew,
  updateQuizData,
  userData,
  setUserData,
  asyncQuiz,
  getTimeDifference,
  userId,
  quizEnded,
  quizInactive,
  setUserDataWhenUnAuthorised,
  isUserApiSuccess,
}: QuizContainerProps) => {
  const [isPresenter, setIsPresenter] = useState(false);
  const quizData = { ...quizDataNew };
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const router = useRouter();

  const { removeUnloadListener } = useUnload((!isPresenter && asyncQuiz) ? outer(quizData.quizId) : unloadFn);

  const updateUser = (loginData: any) => {
    if (loginData) {
      setUserData(loginData);
    }
  };

  const loginConfig = {
    updateUser,
    updateUserField: () => undefined,
    userId: userData.userId,
    removeCloseIcon: true,
  };

  const checkIfLoginNeeded = () => {
    if (quizData.askSignup === AskSignUpType.BEFORE && !userId) {
      // @ts-ignore
      window.loginSignupModule?.init?.('', () => undefined, false, loginConfig, `${process.env.CDN_URL}/`);
    }
  };

  useEffect(() => {
    if (isUserApiSuccess) {
      checkIfLoginNeeded();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserApiSuccess]);

  const checkIfIsPresenter = () => {
    if (userData?.userId === quizData.presenterId) {
      setIsPresenter(true);
    }
  };

  useEffect(() => {
    if (userId) {
      checkIfIsPresenter();

      const params = {
        quizId: Number(quizId),
        userId,
      };

      // adding datadog context
      addContext({ logger: params, rum: params });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    async function fetchLoginData() {
      try {
        const userResp: any = await fetchData({
          method: 'GET',
          url: `/attendee/v1/login-info?shouldCheckForPublicToken=${quizData.askSignup !== AskSignUpType.BEFORE}`,
          credentials: 'include',
        });
        setUserData(userResp.response);
      } catch (e) {
        // @ts-ignore
        if (e?.error?.code === 401) {
          setUserDataWhenUnAuthorised(true);
        }
      } finally {
        setShowLoader(false);
      }
    }
    fetchLoginData();

    getTimeDifference();
    updateQuizData(quizData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnterQuiz = () => {
    router.replace('/vquiz');
  };

  if (quizEnded) {
    return <QuizEnded quizId={quizId} handleEnterQuiz={handleEnterQuiz} />;
  }

  if (quizInactive && !([UserRole.ADMIN].includes(userData?.role) || isPresenter)) {
    return <QuizInactive quizId={quizId} startTime={quizData.startTime} removeUnloadListener={removeUnloadListener} />;
  }

  if (showLoader) {
    return <Loader />;
  }

  if (quizData.askSignup === AskSignUpType.BEFORE && !userId) {
    return null;
  }

  if (quizData.type === QuizType.HYBRID && asyncQuiz) {
    return (
      <MathJaxContext config={config}>
        <AsyncStudentContainer
          quizData={quizData}
          quizId={quizId}
          removeUnloadListener={removeUnloadListener}
        />
      </MathJaxContext>
    );
  }

  if (quizData.type === QuizType.ON_DEMAND) {
    if ([UserRole.ADMIN].includes(userData?.role)) {
      return (
        <MathJaxContext config={config}>
          <PresenterMainComponent
            quizId={quizData.quizId}
            removeUnloadListener={removeUnloadListener}
            isAdmin={[UserRole.ADMIN].includes(userData?.role)}
          />
        </MathJaxContext>
      );
    }
    if (asyncQuiz) {
      return (
        <MathJaxContext config={config}>
          <AsyncStudentContainer
            quizData={quizData}
            quizId={quizId}
            removeUnloadListener={removeUnloadListener}
          />
        </MathJaxContext>
      );
    }
    return (
      <QuizInactive
        quizId={quizId}
        startTime={quizData.startTime}
        removeUnloadListener={removeUnloadListener}
      />
    );
  }

  return (
    <MathJaxContext config={config}>
      {([UserRole.ADMIN].includes(userData?.role) || isPresenter)
        ? (
          <PresenterMainComponent
            quizId={quizData.quizId}
            removeUnloadListener={removeUnloadListener}
            isAdmin={[UserRole.ADMIN].includes(userData?.role)}
          />
        ) : (
          <StudentContainer
            quizData={quizData}
            quizId={quizId}
            removeUnloadListener={removeUnloadListener}
          />
        )}
    </MathJaxContext>
  );
};

export default connector(QuizContainer);
